import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocationReturnPath, openRecord, useNavigateBack } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import TextInput from '../../../components/ui/TextInput';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Switch } from '../../../components/ui/Switch';
import Badge from '../../../components/ui/Badge';
import { useCrewWageColumn, type CrewContractColumn } from './useData';
import { moduleConfig, getInitialFormData, mapItemToFormData } from './config';

type FormState = {
  code: string;
  name: string;
  columnType: 'EARNING' | 'DEDUCTION' | 'INFO';
  dataType: 'DECIMAL' | 'INTEGER';
  includeInTotal: boolean;
  sortOrder: number;
  isActive: boolean;
  notes: string;
};

const COLUMN_TYPE_OPTIONS = [
  {
    value: 'EARNING',
    label: 'Earning',
    description: 'Adds to contract total (basic, OT, leave pay…)',
  },
  {
    value: 'DEDUCTION',
    label: 'Deduction',
    description: 'Subtracts from contract total (allotment, cash…)',
  },
  {
    value: 'INFO',
    label: 'Info',
    description: 'Shown on the sheet but not included in total',
  },
];

const DATA_TYPE_OPTIONS = [
  { value: 'DECIMAL', label: 'Decimal', description: 'Amounts with cents (e.g. 1250.50)' },
  { value: 'INTEGER', label: 'Integer', description: 'Whole numbers (e.g. OT hours)' },
];

const TYPE_BADGE: Record<string, 'success' | 'danger' | 'secondary'> = {
  EARNING: 'success',
  DEDUCTION: 'danger',
  INFO: 'secondary',
};

function normalizeCode(raw: string) {
  return raw.trim().toUpperCase().replace(/\s+/g, '_').slice(0, 30);
}

const FieldShell: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  children: React.ReactNode;
}> = ({ label, required, error, helperText, children }) => {
  const { colors } = useTheme();
  return (
    <div className="space-y-1.5">
      <label className={cn('block text-sm font-medium', colors.text)}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : helperText ? (
        <p className={cn('text-xs', colors.textMuted)}>{helperText}</p>
      ) : null}
    </div>
  );
};

const WageColumnForm: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getLocationReturnPath(location.state);
  const goBack = useNavigateBack(moduleConfig.module.route.list, returnPath);
  const { id } = useParams<{ id: string }>();
  const editId = id ? Number(id) : NaN;
  const isEdit = Number.isFinite(editId);

  const { data, isLoading, isError } = useCrewWageColumn(isEdit ? editId : 0);
  const [form, setForm] = useState<FormState>(() => {
    const init = getInitialFormData();
    return {
      code: String(init.code ?? ''),
      name: String(init.name ?? ''),
      columnType: (init.columnType as FormState['columnType']) ?? 'EARNING',
      dataType: (init.dataType as FormState['dataType']) ?? 'DECIMAL',
      includeInTotal: init.includeInTotal ?? true,
      sortOrder: Number(init.sortOrder ?? 0),
      isActive: init.isActive ?? true,
      notes: String(init.notes ?? ''),
    };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(!isEdit);

  useEffect(() => {
    if (!isEdit || !data) return;
    const mapped = mapItemToFormData(data);
    setForm({
      code: String(mapped.code ?? ''),
      name: String(mapped.name ?? ''),
      columnType: (mapped.columnType as FormState['columnType']) ?? 'EARNING',
      dataType: (mapped.dataType as FormState['dataType']) ?? 'DECIMAL',
      includeInTotal: mapped.includeInTotal ?? true,
      sortOrder: Number(mapped.sortOrder ?? 0),
      isActive: mapped.isActive ?? true,
      notes: String(mapped.notes ?? ''),
    });
    setHydrated(true);
  }, [isEdit, data]);

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const typeMeta = useMemo(
    () => COLUMN_TYPE_OPTIONS.find((o) => o.value === form.columnType),
    [form.columnType],
  );

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.code.trim()) next.code = 'Code is required';
    if (!form.name.trim()) next.name = 'Name is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setSaveError(null);
    const payload: Partial<CrewContractColumn> = {
      code: normalizeCode(form.code),
      name: form.name.trim(),
      columnType: form.columnType,
      dataType: form.dataType,
      includeInTotal: form.columnType === 'INFO' ? false : form.includeInTotal,
      sortOrder: Number.isFinite(form.sortOrder) ? form.sortOrder : 0,
      isActive: form.isActive,
      notes: form.notes.trim() || null,
    };
    try {
      const from = returnPath ?? moduleConfig.module.route.list;
      if (isEdit) {
        await moduleConfig.form.update(editId, payload);
        openRecord(navigate, moduleConfig.module.route.detail(editId), from);
      } else {
        const created = await moduleConfig.form.create(payload);
        openRecord(navigate, moduleConfig.module.route.detail(created.id), from);
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : '';
      setSaveError(msg || moduleConfig.form.errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && isLoading && !hydrated) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        {moduleConfig.form.loadingText}
      </div>
    );
  }

  if (isEdit && (isError || (!isLoading && !data))) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>Wage column not found</h1>
        <p className={cn('mt-2 text-sm', colors.textSecondary)}>This wage column does not exist or was deleted.</p>
        <Link to={moduleConfig.module.route.list} className="mt-4 inline-block text-sm text-blue-600 underline">
          Back to wage columns
        </Link>
      </div>
    );
  }

  const previewCode = normalizeCode(form.code) || 'CODE';
  const previewName = form.name.trim() || 'Column name';
  const sampleAmount = form.dataType === 'INTEGER' ? '40' : '1,250.00';
  const includeInTotalEffective = form.columnType === 'INFO' ? false : form.includeInTotal;

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to={isEdit ? moduleConfig.module.route.detail(editId) : moduleConfig.module.route.list}
            className={cn('text-sm font-medium', colors.textSecondary, 'hover:underline')}
          >
            ← {isEdit ? 'Back to details' : 'Back to wage columns'}
          </Link>
          <h1 className={cn('mt-2 text-2xl font-semibold tracking-tight', colors.text)}>
            {isEdit ? moduleConfig.form.edit.title : moduleConfig.form.new.title}
          </h1>
          <p className={cn('mt-1 max-w-2xl text-sm', colors.textSecondary)}>
            {isEdit
              ? 'Update how this component appears on wage scales and sign-on contracts.'
              : 'Define a contract wage component your company can reuse on scales and S/On.'}
          </p>
        </div>

        {saveError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {saveError}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-12">
          <div className="space-y-5 lg:col-span-7">
            <section className={cn('rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div className="mb-4">
                <h2 className={cn('text-base font-semibold', colors.text)}>Identity</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  Stable code for imports and a clear label for users.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <FieldShell
                    label="Code"
                    required
                    error={errors.code}
                    helperText="Uppercase, unique (e.g. BASIC, ALLOT)."
                  >
                    <TextInput
                      value={form.code}
                      onChange={(e) => setField('code', e.target.value.toUpperCase())}
                      onBlur={() => setField('code', normalizeCode(form.code))}
                      placeholder="BASIC"
                      maxLength={30}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
                <div className="sm:col-span-8">
                  <FieldShell
                    label="Name"
                    required
                    error={errors.name}
                    helperText="Shown on wage scales and the S/On transaction sheet."
                  >
                    <TextInput
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      placeholder="Basic wage"
                      maxLength={100}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
              </div>
            </section>

            <section className={cn('rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div className="mb-4">
                <h2 className={cn('text-base font-semibold', colors.text)}>Behaviour</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  How amounts of this column contribute to the contract total.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-12">
                <div className="sm:col-span-6">
                  <FieldShell label="Column type" required>
                    <Select
                      value={form.columnType}
                      onChange={(e) => {
                        const next = e.target.value as FormState['columnType'];
                        setField('columnType', next);
                        if (next === 'INFO') setField('includeInTotal', false);
                        else setField('includeInTotal', true);
                      }}
                      options={COLUMN_TYPE_OPTIONS}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
                <div className="sm:col-span-6">
                  <FieldShell label="Data type" required>
                    <Select
                      value={form.dataType}
                      onChange={(e) => setField('dataType', e.target.value as FormState['dataType'])}
                      options={DATA_TYPE_OPTIONS}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
                <div className="sm:col-span-12">
                  <div
                    className={cn(
                      'rounded-lg border px-4 py-3',
                      colors.border,
                      form.columnType === 'INFO' ? 'opacity-60' : '',
                    )}
                  >
                    <Switch
                      checked={includeInTotalEffective}
                      onChange={(e) => setField('includeInTotal', e.target.checked)}
                      disabled={form.columnType === 'INFO'}
                      label="Include in contract total"
                      description={
                        form.columnType === 'INFO'
                          ? 'Info columns never affect total wage.'
                          : form.columnType === 'DEDUCTION'
                            ? 'When on, this amount is subtracted from total wage.'
                            : 'When on, this amount is added to total wage.'
                      }
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className={cn('rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div className="mb-4">
                <h2 className={cn('text-base font-semibold', colors.text)}>Display & notes</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  Ordering in grids and whether the column stays available for new scales.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <FieldShell label="Sort order" helperText="Lower numbers appear first.">
                    <TextInput
                      type="number"
                      value={String(form.sortOrder)}
                      onChange={(e) => setField('sortOrder', Number(e.target.value) || 0)}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
                <div className="flex items-end sm:col-span-8">
                  <div className={cn('w-full rounded-lg border px-4 py-3', colors.border)}>
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setField('isActive', e.target.checked)}
                      label="Active"
                      description="Inactive columns stay on existing contracts but are hidden from new scales."
                    />
                  </div>
                </div>
                <div className="sm:col-span-12">
                  <FieldShell label="Notes">
                    <Textarea
                      value={form.notes}
                      onChange={(e) => setField('notes', e.target.value)}
                      placeholder="Optional guidance for office users…"
                      rows={3}
                      maxLength={250}
                      showCharacterCount
                    />
                  </FieldShell>
                </div>
              </div>
            </section>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : isEdit ? moduleConfig.form.edit.submitText : moduleConfig.form.new.submitText}
              </button>
              <button
                type="button"
                onClick={() =>
                  isEdit
                    ? openRecord(navigate, moduleConfig.module.route.detail(editId), returnPath ?? moduleConfig.module.route.list)
                    : goBack()
                }
                className={cn(
                  'inline-flex items-center rounded-lg border px-4 py-2.5 text-sm font-medium transition',
                  colors.border,
                  colors.text,
                  colors.surface,
                  'hover:opacity-90',
                )}
              >
                {moduleConfig.form.cancelText}
              </button>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className={cn('sticky top-6 space-y-4 rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div>
                <h2 className={cn('text-base font-semibold', colors.text)}>Live preview</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  How this column will look on wage scales and S/On.
                </p>
              </div>

              <div className={cn('rounded-lg border p-4', colors.border, colors.background)}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn('font-mono text-xs font-semibold tracking-wide', colors.textMuted)}>
                    {previewCode}
                  </span>
                  <Badge variant={TYPE_BADGE[form.columnType] ?? 'secondary'} className="rounded-md px-2 py-0.5">
                    {typeMeta?.label ?? form.columnType}
                  </Badge>
                  {!form.isActive && (
                    <Badge variant="danger" className="rounded-md px-2 py-0.5">
                      Inactive
                    </Badge>
                  )}
                </div>
                <p className={cn('mt-2 text-lg font-semibold', colors.text)}>{previewName}</p>
                <p className={cn('mt-1 text-sm', colors.textSecondary)}>{typeMeta?.description}</p>
              </div>

              <div className={cn('overflow-hidden rounded-lg border', colors.border)}>
                <div
                  className={cn(
                    'border-b px-3 py-2 text-xs font-medium uppercase tracking-wide',
                    colors.border,
                    colors.textMuted,
                  )}
                >
                  Sample contract row
                </div>
                <div className="grid grid-cols-[1fr_auto] items-center gap-3 px-3 py-3">
                  <div>
                    <div className={cn('text-sm font-medium', colors.text)}>{previewName}</div>
                    <div className={cn('text-xs', colors.textMuted)}>
                      {form.dataType === 'INTEGER' ? 'Whole number' : 'Decimal amount'}
                      {includeInTotalEffective
                        ? form.columnType === 'DEDUCTION'
                          ? ' · deducted from total'
                          : ' · added to total'
                        : ' · not in total'}
                    </div>
                  </div>
                  <div
                    className={cn(
                      'min-w-[5.5rem] rounded-md border px-2.5 py-1.5 text-right font-mono text-sm',
                      colors.border,
                      colors.background,
                      form.columnType === 'DEDUCTION' ? 'text-red-600' : colors.text,
                    )}
                  >
                    {form.columnType === 'DEDUCTION' ? `−${sampleAmount}` : sampleAmount}
                  </div>
                </div>
              </div>

              <ul className={cn('space-y-2 text-sm', colors.textSecondary)}>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  Used when building wage scales by vessel / rank / nationality.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  Imported onto crew S/On transactions, then editable per contract.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  Later snapshotted into MGA period sheets.
                </li>
              </ul>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default WageColumnForm;
